// Client-side handoff for "Continue with Google" on marketing-site growth
// surfaces (the exit-intent modal). idukki-web has no account system of its
// own, so this calls the same idukki-serverless-api endpoints nextapp's own
// Register flow uses (resources/hooks/useGoogleLogin.tsx), passing the
// Google OAuth access_token as { providerId, provider: 'GOOGLE' }. CORS on
// that API is open (Access-Control-Allow-Origin: *), so this works
// cross-origin with no backend change.
//
// A visitor landing here may already have an Idukki account (they're
// reading a compare page mid-evaluation), so /user/register is tried first
// and a 409 (ConflictError — email or Google provider already linked) falls
// back to /login rather than surfacing a dead end.
//
// On success we redirect to `${APP_URL}/dashboard?token=...`. nextapp's
// AuthHandler/Authcontext.js already reads a `?token=` param on any
// `/dashboard*` path, stores it and strips it from the URL — this reuses
// that existing handoff (the same mechanism the Auth0/Feefo SSO callback
// uses) rather than inventing a new one.

const API_BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'https://api.idukki.io'
const APP_URL = process.env.NEXT_PUBLIC_APP_URL ?? 'https://app.idukki.io'

type ApiResult = { status: boolean; token?: string; message?: string }

async function postGoogleToken(path: string, accessToken: string): Promise<{ res: Response; body: ApiResult }> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ providerId: accessToken, provider: 'GOOGLE' }),
  })
  const body = (await res.json().catch(() => ({ status: false }))) as ApiResult
  return { res, body }
}

export type GoogleSignupResult = { ok: true } | { ok: false; message: string }

export async function continueWithGoogle(accessToken: string): Promise<GoogleSignupResult> {
  try {
    let { res, body } = await postGoogleToken('/user/register', accessToken)

    // 409 = ConflictError: this Google account (or its email) is already
    // registered. Try logging in instead of dead-ending the visitor.
    if (res.status === 409) {
      ;({ res, body } = await postGoogleToken('/login', accessToken))
    }

    if (!body.status || !body.token) {
      return { ok: false, message: body.message || 'Could not sign you in. Please try again.' }
    }

    window.location.href = `${APP_URL}/dashboard?token=${encodeURIComponent(body.token)}`
    return { ok: true }
  } catch {
    return { ok: false, message: 'Network error. Please try again.' }
  }
}
